/**
 * Fluxos de atendimento (Prompt 16): cartões ligados num desenho (grafo).
 *
 * O motor aqui é puro (sem banco, sem rede): recebe o desenho, o estado da
 * execução e a resposta da cliente, e devolve o que fazer (efeitos) e o novo
 * estado. O servidor aplica os efeitos (envia, etiqueta, transfere); o
 * editor usa o mesmo motor no simulador — o que se testa é o que roda.
 *
 * Limites da Meta respeitados na validação: até 3 botões (título até 20
 * caracteres), lista com até 10 opções (título 24, descrição 72, botão 20),
 * texto até 1024 caracteres.
 */
import { z } from "zod";

// ---------------------------------------------------------------------------
// Desenho

export const TIPOS_NO = [
  "inicio",
  "mensagem",
  "botoes",
  "lista",
  "pergunta",
  "condicao",
  "horario",
  "espera",
  "etiqueta",
  "transferir",
  "ia",
  "fim",
] as const;
export type TipoNo = (typeof TIPOS_NO)[number];

export const ROTULO_NO: Record<TipoNo, string> = {
  inicio: "Início",
  mensagem: "Mensagem",
  botoes: "Botões",
  lista: "Lista de opções",
  pergunta: "Pergunta",
  condicao: "Condição",
  horario: "Horário da loja",
  espera: "Esperar",
  etiqueta: "Etiqueta",
  transferir: "Passar para a equipe",
  ia: "Entregar para a IA",
  fim: "Fim",
};

export const VALIDACOES_PERGUNTA = ["texto", "numero", "email", "cpf", "data"] as const;
export const OPERADORES = [
  "igual",
  "diferente",
  "contem",
  "preenchido",
  "vazio",
  "maior",
  "menor",
] as const;
export type Operador = (typeof OPERADORES)[number];

export const ROTULO_OPERADOR: Record<Operador, string> = {
  igual: "é igual a",
  diferente: "é diferente de",
  contem: "contém",
  preenchido: "está preenchido",
  vazio: "está vazio",
  maior: "é maior que",
  menor: "é menor que",
};

/** Campos que a condição pode olhar, além das variáveis das perguntas. */
export const CAMPOS_FIXOS = {
  "cliente.cadastrada": "Cliente cadastrada no ERP (sim/não)",
  "cliente.nome": "Nome da cliente",
  "loja.aberta": "Loja aberta agora (sim/não)",
} as const;

const texto = z.string().trim().min(1, "Escreva o texto.").max(1024, "Até 1024 caracteres.");
const idOpcao = z.string().min(1).max(40);
const posicao = z.object({ x: z.number(), y: z.number() });

const base = { id: z.string().min(1).max(40), posicao };

export const noSchema = z.discriminatedUnion("tipo", [
  z.object({ ...base, tipo: z.literal("inicio"), dados: z.object({}).default({}) }),
  z.object({ ...base, tipo: z.literal("mensagem"), dados: z.object({ texto }) }),
  z.object({
    ...base,
    tipo: z.literal("botoes"),
    dados: z.object({
      texto,
      botoes: z
        .array(
          z.object({
            id: idOpcao,
            titulo: z.string().trim().min(1).max(20, "Botão: até 20 caracteres."),
          }),
        )
        .min(1, "Pelo menos 1 botão.")
        .max(3, "Até 3 botões (limite do WhatsApp)."),
    }),
  }),
  z.object({
    ...base,
    tipo: z.literal("lista"),
    dados: z.object({
      texto,
      botao: z.string().trim().min(1).max(20, "Botão da lista: até 20 caracteres."),
      opcoes: z
        .array(
          z.object({
            id: idOpcao,
            titulo: z.string().trim().min(1).max(24, "Opção: até 24 caracteres."),
            descricao: z.string().trim().max(72, "Descrição: até 72 caracteres.").optional(),
          }),
        )
        .min(1, "Pelo menos 1 opção.")
        .max(10, "Até 10 opções (limite do WhatsApp)."),
    }),
  }),
  z.object({
    ...base,
    tipo: z.literal("pergunta"),
    dados: z.object({
      texto,
      variavel: z
        .string()
        .trim()
        .regex(
          /^[a-z][a-z0-9_]{0,29}$/,
          "Variável: letras minúsculas, números e _ (ex.: tamanho).",
        ),
      validacao: z.enum(VALIDACOES_PERGUNTA).default("texto"),
      mensagemInvalida: z.string().trim().max(1024).optional(),
    }),
  }),
  z.object({
    ...base,
    tipo: z.literal("condicao"),
    dados: z.object({
      modo: z.enum(["todas", "alguma"]).default("todas"),
      regras: z
        .array(
          z.object({
            campo: z.string().trim().min(1).max(40),
            operador: z.enum(OPERADORES),
            valor: z.string().trim().max(200).default(""),
          }),
        )
        .min(1, "Pelo menos uma regra."),
    }),
  }),
  z.object({ ...base, tipo: z.literal("horario"), dados: z.object({}).default({}) }),
  z.object({
    ...base,
    tipo: z.literal("espera"),
    // Até 23 h: depois de 24 h sem a cliente escrever, o WhatsApp não deixa enviar.
    dados: z.object({ minutos: z.number().int().min(1).max(1380, "Até 23 horas.") }),
  }),
  z.object({
    ...base,
    tipo: z.literal("etiqueta"),
    dados: z.object({
      acao: z.enum(["adicionar", "remover"]),
      alvo: z.enum(["conversa", "cliente"]).default("conversa"),
      etiquetaId: z.string().uuid("Escolha a etiqueta."),
    }),
  }),
  z.object({
    ...base,
    tipo: z.literal("transferir"),
    dados: z.object({
      /** fila = distribuição normal (prefere a vendedora da cliente). */
      destino: z.enum(["fila", "vendedora"]),
      usuarioId: z.string().uuid().nullable().default(null),
      nota: z.string().trim().max(500).optional(),
    }),
  }),
  z.object({
    ...base,
    tipo: z.literal("ia"),
    dados: z.object({ instrucao: z.string().trim().max(1000).optional() }),
  }),
  z.object({
    ...base,
    tipo: z.literal("fim"),
    dados: z.object({
      texto: z.string().trim().max(1024).optional(),
      concluir: z.boolean().default(false),
    }),
  }),
]);
export type NoFluxo = z.infer<typeof noSchema>;
export type DadosNo<T extends TipoNo> = Extract<NoFluxo, { tipo: T }>["dados"];

export const ligacaoSchema = z.object({
  id: z.string().min(1).max(80),
  origem: z.string().min(1).max(40),
  /** Saída do cartão de origem: "proximo", id do botão/opção, "sim"/"nao"... */
  saida: z.string().min(1).max(40),
  destino: z.string().min(1).max(40),
});
export type LigacaoFluxo = z.infer<typeof ligacaoSchema>;

export const grafoSchema = z.object({
  nos: z.array(noSchema).max(200),
  ligacoes: z.array(ligacaoSchema).max(400),
});
export type GrafoFluxo = z.infer<typeof grafoSchema>;

export const gatilhoFluxoSchema = z.discriminatedUnion("tipo", [
  z.object({
    tipo: z.literal("nova_conversa"),
    publico: z.enum(["todas", "novas", "cadastradas"]).default("todas"),
    quando: z.enum(["sempre", "loja_aberta", "loja_fechada"]).default("sempre"),
  }),
  z.object({
    tipo: z.literal("palavra_chave"),
    palavras: z.array(z.string().trim().min(2).max(60)).min(1).max(20),
    /** Também quando a conversa já está com alguém da equipe. */
    comResponsavel: z.boolean().default(false),
  }),
  z.object({ tipo: z.literal("manual") }),
]);
export type GatilhoFluxo = z.infer<typeof gatilhoFluxoSchema>;

export const ROTULO_GATILHO_FLUXO: Record<GatilhoFluxo["tipo"], string> = {
  nova_conversa: "Conversa nova",
  palavra_chave: "Palavra-chave",
  manual: "Iniciado pela equipe",
};

/** Saídas de cada cartão (as alças de ligação). */
export function saidasDoNo(no: NoFluxo): { id: string; rotulo: string }[] {
  switch (no.tipo) {
    case "inicio":
    case "mensagem":
    case "espera":
    case "etiqueta":
      return [{ id: "proximo", rotulo: "" }];
    case "pergunta":
      return [
        { id: "proximo", rotulo: "Respondeu" },
        { id: "invalida", rotulo: "Não respondeu certo" },
      ];
    case "botoes":
      return [
        ...no.dados.botoes.map((b) => ({ id: b.id, rotulo: b.titulo })),
        { id: "invalida", rotulo: "Outra resposta" },
      ];
    case "lista":
      return [
        ...no.dados.opcoes.map((o) => ({ id: o.id, rotulo: o.titulo })),
        { id: "invalida", rotulo: "Outra resposta" },
      ];
    case "condicao":
      return [
        { id: "sim", rotulo: "Sim" },
        { id: "nao", rotulo: "Não" },
      ];
    case "horario":
      return [
        { id: "aberta", rotulo: "Aberta" },
        { id: "fechada", rotulo: "Fechada" },
      ];
    case "transferir":
    case "ia":
    case "fim":
      return [];
  }
}

/** Problemas que impedem publicar (mensagens para a tela). */
export function validarGrafo(g: GrafoFluxo): string[] {
  const problemas: string[] = [];
  const r = grafoSchema.safeParse(g);
  if (!r.success) {
    for (const i of r.error.issues.slice(0, 5)) {
      const no = typeof i.path[1] === "number" ? g.nos[i.path[1]] : undefined;
      problemas.push(no ? `${ROTULO_NO[no.tipo] ?? "Cartão"}: ${i.message}` : i.message);
    }
    return problemas;
  }
  const porId = new Map(g.nos.map((n) => [n.id, n]));
  const inicios = g.nos.filter((n) => n.tipo === "inicio");
  if (inicios.length !== 1) problemas.push("O fluxo precisa de exatamente um cartão Início.");
  if (porId.size !== g.nos.length) problemas.push("Há cartões com o mesmo identificador.");
  const usadas = new Set<string>();
  for (const l of g.ligacoes) {
    const o = porId.get(l.origem);
    const d = porId.get(l.destino);
    if (!o || !d) {
      problemas.push("Há uma ligação para um cartão que não existe.");
      continue;
    }
    if (d.tipo === "inicio") problemas.push("Nada pode ligar de volta ao Início.");
    if (!saidasDoNo(o).some((s) => s.id === l.saida))
      problemas.push(`${ROTULO_NO[o.tipo]}: ligação saindo de uma opção que não existe mais.`);
    const chave = `${l.origem}:${l.saida}`;
    if (usadas.has(chave))
      problemas.push(`${ROTULO_NO[o.tipo]}: uma mesma saída ligada a dois cartões.`);
    usadas.add(chave);
  }
  const inicio = inicios[0];
  if (inicio && !g.ligacoes.some((l) => l.origem === inicio.id))
    problemas.push("Ligue o Início ao primeiro cartão.");
  for (const n of g.nos) {
    if (n.tipo === "botoes" || n.tipo === "lista") {
      const ids =
        n.tipo === "botoes" ? n.dados.botoes.map((b) => b.id) : n.dados.opcoes.map((o) => o.id);
      if (new Set(ids).size !== ids.length)
        problemas.push(`${ROTULO_NO[n.tipo]}: opções repetidas.`);
      const titulos = (n.tipo === "botoes" ? n.dados.botoes : n.dados.opcoes).map((x) =>
        normalizar(x.titulo),
      );
      if (new Set(titulos).size !== titulos.length)
        problemas.push(`${ROTULO_NO[n.tipo]}: dois botões ou opções com o mesmo texto.`);
    }
  }
  // Cartões soltos (não alcançáveis a partir do Início).
  if (inicio) {
    const alcancados = new Set([inicio.id]);
    const pilha = [inicio.id];
    while (pilha.length) {
      const atual = pilha.pop()!;
      for (const l of g.ligacoes)
        if (l.origem === atual && !alcancados.has(l.destino)) {
          alcancados.add(l.destino);
          pilha.push(l.destino);
        }
    }
    const soltos = g.nos.filter((n) => !alcancados.has(n.id));
    if (soltos.length)
      problemas.push(
        `${soltos.length} cartão(ões) sem ligação a partir do Início (${soltos
          .map((s) => ROTULO_NO[s.tipo])
          .slice(0, 3)
          .join(", ")}).`,
      );
  }
  return [...new Set(problemas)];
}

/** Desenho inicial de um fluxo novo. */
export function grafoInicial(): GrafoFluxo {
  return {
    nos: [
      { id: "inicio", tipo: "inicio", posicao: { x: 0, y: 0 }, dados: {} },
      {
        id: "boas_vindas",
        tipo: "mensagem",
        posicao: { x: 320, y: 0 },
        dados: { texto: "Oi, {nome}! Que bom ter você aqui na {loja}." },
      },
    ],
    ligacoes: [
      { id: "inicio-boas_vindas", origem: "inicio", saida: "proximo", destino: "boas_vindas" },
    ],
  };
}

// ---------------------------------------------------------------------------
// Motor

export interface EstadoFluxo {
  /** Cartão em que a execução parou (esperando resposta ou tempo). */
  noAtual: string | null;
  variaveis: Record<string, string>;
  aguardando: "resposta" | "espera" | null;
  /** Respostas inválidas seguidas no cartão atual. */
  invalidas: number;
}

export interface ContextoFluxo {
  cliente: { nome: string | null; cadastrada: boolean };
  loja: { nome: string; aberta: boolean };
}

export type EfeitoFluxo =
  | { tipo: "mensagem"; texto: string }
  | { tipo: "botoes"; texto: string; botoes: { id: string; titulo: string }[] }
  | {
      tipo: "lista";
      texto: string;
      botao: string;
      opcoes: { id: string; titulo: string; descricao?: string }[];
    }
  | {
      tipo: "etiqueta";
      acao: "adicionar" | "remover";
      alvo: "conversa" | "cliente";
      etiquetaId: string;
    }
  | { tipo: "esperar"; minutos: number }
  | {
      tipo: "transferir";
      destino: "fila" | "vendedora";
      usuarioId: string | null;
      nota: string | null;
    }
  | { tipo: "ia"; instrucao: string | null }
  | { tipo: "fim"; concluir: boolean };

export type EntradaFluxo =
  | { tipo: "resposta"; texto: string | null; respostaId: string | null }
  | { tipo: "espera_concluida" };

export interface PassoFluxo {
  efeitos: EfeitoFluxo[];
  estado: EstadoFluxo;
  /** Acabou: a conversa volta ao caminho normal (equipe, IA ou encerrada). */
  terminou: boolean;
}

const MAX_PASSOS = 60;
/** Respostas erradas seguidas antes de seguir pela saída "não respondeu certo". */
export const MAX_INVALIDAS = 2;

export function normalizar(t: string): string {
  return t.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\s+/g, " ").trim();
}

function primeiroNome(nome: string | null): string {
  return (nome ?? "").trim().split(/\s+/)[0] ?? "";
}

/** Troca {nome}, {loja} e {variavel} pelos valores. Variável sem valor some. */
export function preencherTexto(t: string, estado: EstadoFluxo, ctx: ContextoFluxo): string {
  const valores: Record<string, string> = {
    ...estado.variaveis,
    nome: primeiroNome(ctx.cliente.nome),
    nome_completo: ctx.cliente.nome ?? "",
    loja: ctx.loja.nome,
  };
  return (
    t
      .replace(/\{([a-z][a-z0-9_]*)\}/gi, (_m, k: string) => valores[k.toLowerCase()] ?? "")
      // Variável vazia não deixa buraco: "Oi, {nome}!" vira "Oi!".
      .replace(/[ \t]{2,}/g, " ")
      .replace(/ +([!?,])/g, "$1")
      .replace(/,([!?.])/g, "$1")
      .trim()
  );
}

function cpfValido(t: string): boolean {
  const d = t.replace(/\D/g, "");
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  const digito = (n: number) => {
    let s = 0;
    for (let i = 0; i < n; i++) s += Number(d[i]) * (n + 1 - i);
    const r = (s * 10) % 11;
    return r === 10 ? 0 : r;
  };
  return digito(9) === Number(d[9]) && digito(10) === Number(d[10]);
}

/** Valida e normaliza a resposta de uma pergunta; null = inválida. */
export function validarResposta(
  validacao: (typeof VALIDACOES_PERGUNTA)[number],
  bruto: string,
): string | null {
  const t = bruto.trim();
  if (!t) return null;
  switch (validacao) {
    case "texto":
      return t.slice(0, 500);
    case "numero": {
      const m = /-?\d+(?:[.,]\d+)?/.exec(t);
      return m ? m[0].replace(",", ".") : null;
    }
    case "email": {
      const m = /[^\s@]+@[^\s@]+\.[^\s@]+/.exec(t);
      return m ? m[0].toLowerCase() : null;
    }
    case "cpf":
      return cpfValido(t) ? t.replace(/\D/g, "") : null;
    case "data": {
      const m = /^(\d{1,2})[/.-](\d{1,2})(?:[/.-](\d{2,4}))?$/.exec(t);
      if (!m) return null;
      const dia = Number(m[1]);
      const mes = Number(m[2]);
      if (dia < 1 || dia > 31 || mes < 1 || mes > 12) return null;
      const ano = m[3] ? (m[3].length === 2 ? `20${m[3]}` : m[3]) : null;
      return `${String(dia).padStart(2, "0")}/${String(mes).padStart(2, "0")}${ano ? `/${ano}` : ""}`;
    }
  }
}

const MENSAGEM_INVALIDA: Record<(typeof VALIDACOES_PERGUNTA)[number], string> = {
  texto: "Não consegui entender. Pode escrever de novo?",
  numero: "Pode me responder só com o número?",
  email: "Esse e-mail parece incompleto. Pode conferir?",
  cpf: "Esse CPF não confere. Pode digitar de novo, só os números?",
  data: "Pode me mandar a data assim: dia/mês (ex.: 15/11)?",
};

function valorDoCampo(campo: string, estado: EstadoFluxo, ctx: ContextoFluxo): string {
  if (campo === "cliente.cadastrada") return ctx.cliente.cadastrada ? "sim" : "nao";
  if (campo === "cliente.nome") return ctx.cliente.nome ?? "";
  if (campo === "loja.aberta") return ctx.loja.aberta ? "sim" : "nao";
  return estado.variaveis[campo] ?? "";
}

function avaliarRegra(
  r: DadosNo<"condicao">["regras"][number],
  estado: EstadoFluxo,
  ctx: ContextoFluxo,
): boolean {
  const atual = normalizar(valorDoCampo(r.campo, estado, ctx));
  const esperado = normalizar(r.valor);
  const n = (s: string) => Number(s.replace(",", "."));
  switch (r.operador) {
    case "igual":
      return atual === esperado;
    case "diferente":
      return atual !== esperado;
    case "contem":
      return !!esperado && atual.includes(esperado);
    case "preenchido":
      return atual !== "";
    case "vazio":
      return atual === "";
    case "maior":
      return atual !== "" && Number.isFinite(n(atual)) && n(atual) > n(esperado);
    case "menor":
      return atual !== "" && Number.isFinite(n(atual)) && n(atual) < n(esperado);
  }
}

/** Qual opção a cliente escolheu: pelo botão, pelo número ou pelo texto. */
export function escolherOpcao(
  opcoes: { id: string; titulo: string }[],
  entrada: { texto: string | null; respostaId: string | null },
): string | null {
  if (entrada.respostaId && opcoes.some((o) => o.id === entrada.respostaId))
    return entrada.respostaId;
  const t = normalizar(entrada.texto ?? "");
  if (!t) return null;
  const numero = /^(\d{1,2})[).]?$/.exec(t);
  if (numero) return opcoes[Number(numero[1]) - 1]?.id ?? null;
  const exata = opcoes.find((o) => normalizar(o.titulo) === t);
  if (exata) return exata.id;
  const contida = opcoes.filter((o) => t.includes(normalizar(o.titulo)));
  return contida.length === 1 ? contida[0]!.id : null;
}

function seguinte(g: GrafoFluxo, origem: string, saida: string): string | null {
  return g.ligacoes.find((l) => l.origem === origem && l.saida === saida)?.destino ?? null;
}

/**
 * Anda pelo desenho a partir de `noId` até precisar esperar (resposta ou
 * tempo) ou terminar. Saída sem ligação = fim (a conversa volta à equipe).
 */
function andar(
  g: GrafoFluxo,
  noId: string | null,
  estado: EstadoFluxo,
  ctx: ContextoFluxo,
  efeitos: EfeitoFluxo[],
): PassoFluxo {
  const porId = new Map(g.nos.map((n) => [n.id, n]));
  let atual = noId;
  for (let passos = 0; passos < MAX_PASSOS; passos++) {
    const no = atual ? porId.get(atual) : undefined;
    if (!no)
      return { efeitos, estado: { ...estado, noAtual: null, aguardando: null }, terminou: true };
    const p = (t: string) => preencherTexto(t, estado, ctx);
    switch (no.tipo) {
      case "inicio":
        atual = seguinte(g, no.id, "proximo");
        break;
      case "mensagem":
        efeitos.push({ tipo: "mensagem", texto: p(no.dados.texto) });
        atual = seguinte(g, no.id, "proximo");
        break;
      case "botoes":
        efeitos.push({ tipo: "botoes", texto: p(no.dados.texto), botoes: no.dados.botoes });
        return {
          efeitos,
          estado: { ...estado, noAtual: no.id, aguardando: "resposta", invalidas: 0 },
          terminou: false,
        };
      case "lista":
        efeitos.push({
          tipo: "lista",
          texto: p(no.dados.texto),
          botao: no.dados.botao,
          opcoes: no.dados.opcoes,
        });
        return {
          efeitos,
          estado: { ...estado, noAtual: no.id, aguardando: "resposta", invalidas: 0 },
          terminou: false,
        };
      case "pergunta":
        efeitos.push({ tipo: "mensagem", texto: p(no.dados.texto) });
        return {
          efeitos,
          estado: { ...estado, noAtual: no.id, aguardando: "resposta", invalidas: 0 },
          terminou: false,
        };
      case "condicao": {
        const r = no.dados.regras.map((x) => avaliarRegra(x, estado, ctx));
        const ok = no.dados.modo === "todas" ? r.every(Boolean) : r.some(Boolean);
        atual = seguinte(g, no.id, ok ? "sim" : "nao");
        break;
      }
      case "horario":
        atual = seguinte(g, no.id, ctx.loja.aberta ? "aberta" : "fechada");
        break;
      case "espera":
        efeitos.push({ tipo: "esperar", minutos: no.dados.minutos });
        return {
          efeitos,
          estado: { ...estado, noAtual: no.id, aguardando: "espera", invalidas: 0 },
          terminou: false,
        };
      case "etiqueta":
        efeitos.push({ tipo: "etiqueta", ...no.dados });
        atual = seguinte(g, no.id, "proximo");
        break;
      case "transferir":
        efeitos.push({
          tipo: "transferir",
          destino: no.dados.destino,
          usuarioId: no.dados.usuarioId,
          nota: no.dados.nota ? p(no.dados.nota) : null,
        });
        return { efeitos, estado: { ...estado, noAtual: null, aguardando: null }, terminou: true };
      case "ia":
        efeitos.push({ tipo: "ia", instrucao: no.dados.instrucao ? p(no.dados.instrucao) : null });
        return { efeitos, estado: { ...estado, noAtual: null, aguardando: null }, terminou: true };
      case "fim":
        if (no.dados.texto) efeitos.push({ tipo: "mensagem", texto: p(no.dados.texto) });
        efeitos.push({ tipo: "fim", concluir: no.dados.concluir });
        return { efeitos, estado: { ...estado, noAtual: null, aguardando: null }, terminou: true };
    }
  }
  // Ciclo sem espera (ex.: condição ligada nela mesma): para com segurança.
  return { efeitos, estado: { ...estado, noAtual: null, aguardando: null }, terminou: true };
}

export function iniciarFluxo(g: GrafoFluxo, ctx: ContextoFluxo): PassoFluxo {
  const inicio = g.nos.find((n) => n.tipo === "inicio");
  const estado: EstadoFluxo = { noAtual: null, variaveis: {}, aguardando: null, invalidas: 0 };
  return andar(g, inicio?.id ?? null, estado, ctx, []);
}

export function continuarFluxo(
  g: GrafoFluxo,
  estado: EstadoFluxo,
  entrada: EntradaFluxo,
  ctx: ContextoFluxo,
): PassoFluxo {
  const no = g.nos.find((n) => n.id === estado.noAtual);
  if (!no || !estado.aguardando)
    return { efeitos: [], estado: { ...estado, noAtual: null, aguardando: null }, terminou: true };

  if (estado.aguardando === "espera") {
    // Mensagem da cliente durante a espera não acelera nada.
    if (entrada.tipo !== "espera_concluida") return { efeitos: [], estado, terminou: false };
    return andar(g, seguinte(g, no.id, "proximo"), { ...estado, aguardando: null }, ctx, []);
  }
  if (entrada.tipo !== "resposta") return { efeitos: [], estado, terminou: false };

  const invalida = (repetir: EfeitoFluxo[]): PassoFluxo => {
    const invalidas = estado.invalidas + 1;
    if (invalidas >= MAX_INVALIDAS)
      return andar(
        g,
        seguinte(g, no.id, "invalida"),
        { ...estado, aguardando: null, invalidas: 0 },
        ctx,
        [],
      );
    return { efeitos: repetir, estado: { ...estado, invalidas }, terminou: false };
  };

  switch (no.tipo) {
    case "botoes":
    case "lista": {
      const opcoes = no.tipo === "botoes" ? no.dados.botoes : no.dados.opcoes;
      const escolhida = escolherOpcao(opcoes, entrada);
      if (!escolhida) {
        const texto = preencherTexto(no.dados.texto, estado, { ...ctx });
        return invalida([
          no.tipo === "botoes"
            ? { tipo: "botoes", texto: `Não entendi. ${texto}`, botoes: no.dados.botoes }
            : {
                tipo: "lista",
                texto: `Não entendi. ${texto}`,
                botao: no.dados.botao,
                opcoes: no.dados.opcoes,
              },
        ]);
      }
      const titulo = opcoes.find((o) => o.id === escolhida)!.titulo;
      // A escolha também fica guardada: {resposta_<id do cartão>}.
      const variaveis = { ...estado.variaveis, [`escolha_${no.id}`]: titulo };
      return andar(
        g,
        seguinte(g, no.id, escolhida),
        { ...estado, variaveis, aguardando: null, invalidas: 0 },
        ctx,
        [],
      );
    }
    case "pergunta": {
      const valor = validarResposta(no.dados.validacao, entrada.texto ?? "");
      if (valor === null)
        return invalida([
          {
            tipo: "mensagem",
            texto: no.dados.mensagemInvalida || MENSAGEM_INVALIDA[no.dados.validacao],
          },
        ]);
      const variaveis = { ...estado.variaveis, [no.dados.variavel]: valor };
      return andar(
        g,
        seguinte(g, no.id, "proximo"),
        { ...estado, variaveis, aguardando: null, invalidas: 0 },
        ctx,
        [],
      );
    }
    default:
      return {
        efeitos: [],
        estado: { ...estado, noAtual: null, aguardando: null },
        terminou: true,
      };
  }
}

/** A mensagem dispara o gatilho de palavra-chave? */
export function casaPalavraChave(palavras: string[], texto: string | null): boolean {
  const t = ` ${normalizar(texto ?? "").replace(/[^\p{L}\p{N} ]/gu, " ")} `;
  return palavras.some((p) => {
    const alvo = normalizar(p)
      .replace(/[^\p{L}\p{N} ]/gu, " ")
      .trim();
    return !!alvo && t.includes(` ${alvo} `);
  });
}

// ---------------------------------------------------------------------------
// API (tela)

/**
 * Rascunho (salvo a cada mudança): aceita cartão incompleto (texto apagado,
 * etiqueta ainda não escolhida). A validação completa é ao publicar.
 */
export const rascunhoSchema = z.object({
  nos: z
    .array(
      z
        .object({
          id: z.string().min(1).max(40),
          tipo: z.enum(TIPOS_NO),
          posicao,
          dados: z.record(z.unknown()),
        })
        .passthrough(),
    )
    .max(200),
  ligacoes: z.array(ligacaoSchema).max(400),
});

export const salvarFluxoSchema = z.object({
  nome: z.string().trim().min(1, "Dê um nome ao fluxo.").max(80),
  descricao: z.string().trim().max(300).nullable().default(null),
  gatilho: gatilhoFluxoSchema,
  prioridade: z.number().int().min(1).max(999).default(100),
  rascunho: rascunhoSchema.transform((g) => g as unknown as GrafoFluxo),
});
export type SalvarFluxo = z.infer<typeof salvarFluxoSchema>;

export const STATUS_EXECUCAO = [
  "ativa",
  "concluida",
  "transferida",
  "ia",
  "cancelada",
  "abandonada",
  "erro",
] as const;
export type StatusExecucao = (typeof STATUS_EXECUCAO)[number];

export const ROTULO_STATUS_EXECUCAO: Record<StatusExecucao, string> = {
  ativa: "Em andamento",
  concluida: "Concluído",
  transferida: "Passado para a equipe",
  ia: "Entregue para a IA",
  cancelada: "Interrompido pela equipe",
  abandonada: "Cliente não respondeu",
  erro: "Erro",
};

export interface ResumoFluxo {
  id: string;
  nome: string;
  descricao: string | null;
  gatilho: GatilhoFluxo;
  ativo: boolean;
  prioridade: number;
  versaoPublicada: number | null;
  publicadoEm: string | null;
  rascunhoAlterado: boolean;
  execucoes7d: { total: number; concluidas: number; transferidas: number; abandonadas: number };
  atualizadoEm: string;
}

export interface Fluxo extends ResumoFluxo {
  rascunho: GrafoFluxo;
}

export interface ExecucaoNaConversa {
  id: string;
  fluxoId: string;
  fluxoNome: string;
  status: StatusExecucao;
  aguardando: "resposta" | "espera" | null;
  iniciadaEm: string;
}
